import React, { useState } from 'react';
import {
  Table,
  Spinner,
  Form,
  Row,
  Col,
  Button,
  Alert,
  Badge,
  CardFooter,
  CardTitle,
  ProgressBar
} from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Card, CardBody, CardHeader } from '@components/card/card';
import { ntxAPI } from '@API/axiosNeatex';
import useGetItemsList from '@hooks/INV/MasterItems/useGetItemsList';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import type { APIResponse } from '@store/Shared/shared';

import Loading from '@components/Shared/Loader/Loading';

interface CostVsSales {
  itemId: string;
  itemCode: string;
  itemName: string;
  itemName2: string;
  costPrice: number;
  salesPrice: number;
  varianceAmount: number;
  variancePercent: number;
}

const CostVsSalesReport: React.FC = () => {
  const [data, setData] = useState<CostVsSales[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const { ItemsOptions, loading: loading1, error: error1 } = useGetItemsList();
  const [itemId, setItemId] = useState<string | null>(null);
  const { t, i18n } = useTranslation();

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [startDate, endDate] = dateRange;

  const [profitabilityFilter, setProfitabilityFilter] = useState<string>('all');
  const [varianceThreshold, setVarianceThreshold] = useState<number>(0);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await ntxAPI.get<APIResponse<CostVsSales[]>>(`InventoryReports/cost-vs-sales`, {
        params: {
          ItemId: itemId,
          StartDate: startDate,
          EndDate: endDate
        }
      });

      if (data.succeeded === true) {
        setData(data.data);
      } else {
        setError(data instanceof Error ? data.message : t('UnknownError'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('UnknownError'));
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setItemId(null);
    setDateRange([null, null]);
    setProfitabilityFilter('all');
    setVarianceThreshold(0);
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US').format(amount);
  };

  const formatPercentage = (percent: number): string => {
    return `${percent?.toFixed(2)}%`;
  };

  // Get profitability badge color
  const getProfitabilityBadge = (variancePercent: number) => {
    if (variancePercent > 0) return { color: 'success', text: i18n.language === 'en' ? 'Profitable' : 'مربح' };
    if (variancePercent < 0) return { color: 'danger', text: i18n.language === 'en' ? 'Loss' : 'خسارة' };
    return { color: 'warning', text: i18n.language === 'en' ? 'Break Even' : 'تعادل' };
  };

  // Get variance badge color
  const getVarianceBadgeColor = (variancePercent: number) => {
    if (variancePercent > 20) return 'success';
    if (variancePercent > 10) return 'info';
    if (variancePercent > 0) return 'warning';
    if (variancePercent < -10) return 'danger';
    return 'secondary';
  };

  // Calculate profitability statistics
  const calculateProfitabilityStats = () => {
    const profitableItems = data.filter(item => item.variancePercent > 0).length;
    const lossItems = data.filter(item => item.variancePercent < 0).length;
    const breakEvenItems = data.filter(item => item.variancePercent === 0).length;
    const totalVariance = data.reduce((sum, item) => sum + item.varianceAmount, 0);
    const avgMargin = data.length > 0 ? data.reduce((sum, item) => sum + item.variancePercent, 0) / data.length : 0;

    return { profitableItems, lossItems, breakEvenItems, totalVariance, avgMargin };
  };

  // Filter data based on profitability and variance threshold
  const filteredData = data.filter(item => {
    if (profitabilityFilter === 'profitable' && item.variancePercent <= 0) return false;
    if (profitabilityFilter === 'loss' && item.variancePercent >= 0) return false;
    if (profitabilityFilter === 'breakEven' && item.variancePercent !== 0) return false;
    if (Math.abs(item.variancePercent) < varianceThreshold) return false;
    return true;
  });

  const { profitableItems, lossItems, breakEvenItems, totalVariance, avgMargin } = calculateProfitabilityStats();

  return (
    <div className='container mt-4'>
      <Card>
        <CardHeader className='d-flex justify-content-between align-items-center'>
          <h5 className='mb-0'>
            {i18n.language === 'en' ? 'Cost vs Sales Analysis Report' : 'تقرير تحليل التكلفة مقابل المبيعات'}
          </h5>
          {data.length > 0 && (
            <div>
              <span className='me-2'>
                {i18n.language === 'en'
                  ? `Showing: ${filteredData.length} of ${data.length} items`
                  : `عرض: ${filteredData.length} من ${data.length} عنصر`}
              </span>
            </div>
          )}
        </CardHeader>

        <CardBody>
          {error && (
            <Alert variant='danger' className='mb-4'>
              {i18n.language === 'en' ? 'Error loading data:' : 'خطأ في تحميل البيانات:'} {error}
            </Alert>
          )}

          <Card className='mb-4'>
            <CardHeader>{i18n.language === 'en' ? 'Filters' : 'الفلاتر'}</CardHeader>
            <Loading loading={loading1} error={error1}>
              <CardBody>
                <Form>
                  <Row className='g-3'>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>{t('itemName')}</Form.Label>
                        <Select
                          classNamePrefix='react-select'
                          isSearchable
                          isClearable
                          options={ItemsOptions}
                          onChange={(option: any) => setItemId(option?.value || null)}
                          value={ItemsOptions.find(f => f.value === itemId)}
                          placeholder={t('Choose')}
                          menuPortalTarget={document.body}
                          styles={{
                            menuPortal: (base: any) => ({ ...base, zIndex: 9999 })
                          }}
                        />
                      </Form.Group>
                    </Col>

                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>{i18n.language === 'en' ? 'Date Range' : 'نطاق التاريخ'}</Form.Label>
                        <DatePicker
                          selectsRange={true}
                          startDate={startDate}
                          endDate={endDate}
                          onChange={update => setDateRange(update)}
                          isClearable={true}
                          className='form-control text-center'
                          placeholderText={i18n.language === 'en' ? 'Select date range' : 'اختر نطاق التاريخ'}
                        />
                      </Form.Group>
                    </Col>

                    <Col md={3}>
                      <Form.Group controlId='profitabilityFilter'>
                        <Form.Label>{i18n.language === 'en' ? 'Profitability' : 'الربحية'}</Form.Label>
                        <Form.Select value={profitabilityFilter} onChange={e => setProfitabilityFilter(e.target.value)}>
                          <option value='all'>{i18n.language === 'en' ? 'All Items' : 'جميع العناصر'}</option>
                          <option value='profitable'>
                            {i18n.language === 'en' ? 'Profitable Only' : 'المربحة فقط'}
                          </option>
                          <option value='loss'>{i18n.language === 'en' ? 'Loss Making Only' : 'الخاسرة فقط'}</option>
                          <option value='breakEven'>
                            {i18n.language === 'en' ? 'Break Even Only' : 'المتعادلة فقط'}
                          </option>
                        </Form.Select>
                      </Form.Group>
                    </Col>

                    <Col md={3}>
                      <Form.Group controlId='varianceThresholdFilter'>
                        <Form.Label>{i18n.language === 'en' ? 'Variance Threshold (%)' : 'حد التفاوت (%)'}</Form.Label>
                        <Form.Range
                          min='0'
                          max='100'
                          step='1'
                          value={varianceThreshold}
                          onChange={e => setVarianceThreshold(Number(e.target.value))}
                        />
                        <Form.Text className='text-muted'>
                          {i18n.language === 'en'
                            ? `Min variance: ${varianceThreshold}%`
                            : `أقل تفاوت: ${varianceThreshold}%`}
                        </Form.Text>
                      </Form.Group>
                    </Col>

                    <Col xs={12} className={`text-end  `}>
                      <Button variant='secondary' onClick={resetFilters} className='me-2'>
                        {i18n.language === 'en' ? 'Reset Filters' : 'إعادة تعيين الفلاتر'}
                      </Button>
                      <Button variant='primary' onClick={fetchData} disabled={loading}>
                        {loading ? (
                          <>
                            <Spinner
                              as='span'
                              animation='border'
                              size='sm'
                              role='status'
                              aria-hidden='true'
                              className='me-2'
                            />
                            {i18n.language === 'en' ? 'Loading...' : 'جاري التحميل...'}
                          </>
                        ) : i18n.language === 'en' ? (
                          'Load Report'
                        ) : (
                          'تحميل التقرير'
                        )}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </CardBody>
            </Loading>
          </Card>

          {/* Profitability Summary */}
          {data.length > 0 && (
            <Row className='mb-4'>
              <Col md={2}>
                <Card className='text-center border-0 bg-secondary bg-opacity-30'>
                  <CardBody>
                    <CardTitle className='text-primary'>
                      {i18n.language === 'en' ? 'Total Items' : 'إجمالي الأصناف'}
                    </CardTitle>
                    <h3 className='text-primary'>{data.length}</h3>
                  </CardBody>
                </Card>
              </Col>
              <Col md={2}>
                <Card className='text-center border-0 bg-secondary bg-opacity-30'>
                  <CardBody>
                    <CardTitle className='text-success'>{i18n.language === 'en' ? 'Profitable' : 'مربحة'}</CardTitle>
                    <h3 className='text-success'>{profitableItems}</h3>
                    <small>{((profitableItems / data.length) * 100)?.toFixed(1)}%</small>
                  </CardBody>
                </Card>
              </Col>
              <Col md={2}>
                <Card className='text-center border-0 bg-secondary bg-opacity-30'>
                  <CardBody>
                    <CardTitle className='text-danger'>{i18n.language === 'en' ? 'Loss Making' : 'خاسرة'}</CardTitle>
                    <h3 className='text-danger'>{lossItems}</h3>
                    <small>{((lossItems / data.length) * 100)?.toFixed(1)}%</small>
                  </CardBody>
                </Card>
              </Col>
              <Col md={2}>
                <Card className='text-center border-0 bg-secondary bg-opacity-30'>
                  <CardBody>
                    <CardTitle className='text-warning'>{i18n.language === 'en' ? 'Break Even' : 'متعادلة'}</CardTitle>
                    <h3 className='text-warning'>{breakEvenItems}</h3>
                    <small>{((breakEvenItems / data.length) * 100)?.toFixed(1)}%</small>
                  </CardBody>
                </Card>
              </Col>
              <Col md={2}>
                <Card className='text-center border-0 bg-secondary bg-opacity-30'>
                  <CardBody>
                    <CardTitle className='text-info'>
                      {i18n.language === 'en' ? 'Total Variance' : 'إجمالي التفاوت'}
                    </CardTitle>
                    <h3 className={totalVariance >= 0 ? 'text-success' : 'text-danger'}>
                      {formatCurrency(totalVariance)}
                    </h3>
                  </CardBody>
                </Card>
              </Col>
              <Col md={2}>
                <Card className='text-center border-0 bg-secondary bg-opacity-30'>
                  <CardBody>
                    <CardTitle className='text-secondary'>
                      {i18n.language === 'en' ? 'Avg Margin' : 'متوسط الهامش'}
                    </CardTitle>
                    <h3 className={avgMargin >= 0 ? 'text-success' : 'text-danger'}>{formatPercentage(avgMargin)}</h3>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}

          {/* Profitability Distribution */}
          {data.length > 0 && (
            <Card className='mb-4'>
              <CardHeader>
                <h6 className='mb-0'>{i18n.language === 'en' ? 'Profitability Distribution' : 'توزيع الربحية'}</h6>
              </CardHeader>
              <CardBody>
                <Row className='align-items-center'>
                  <Col md={3}>
                    <div className='text-center'>
                      <div className='mb-2'>
                        <Badge bg='success' className='me-2'>
                          {i18n.language === 'en' ? 'Profitable' : 'مربحة'}
                        </Badge>
                        <Badge bg='danger' className='me-2'>
                          {i18n.language === 'en' ? 'Loss' : 'خاسرة'}
                        </Badge>
                        <Badge bg='warning'>{i18n.language === 'en' ? 'Break Even' : 'متعادلة'}</Badge>
                      </div>
                    </div>
                  </Col>
                  <Col md={9}>
                    <ProgressBar style={{ height: '25px' }}>
                      <ProgressBar
                        variant='success'
                        now={(profitableItems / data.length) * 100}
                        key={1}
                        label={`${profitableItems} ${i18n.language === 'en' ? 'Profitable' : 'مربحة'}`}
                      />
                      <ProgressBar
                        variant='danger'
                        now={(lossItems / data.length) * 100}
                        key={2}
                        label={`${lossItems} ${i18n.language === 'en' ? 'Loss' : 'خاسرة'}`}
                      />
                      <ProgressBar
                        variant='warning'
                        now={(breakEvenItems / data.length) * 100}
                        key={3}
                        label={`${breakEvenItems} ${i18n.language === 'en' ? 'Break Even' : 'متعادلة'}`}
                      />
                    </ProgressBar>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          )}

          {/* Results Section */}
          {loading ? (
            <div className='text-center my-5'>
              <Spinner animation='border' role='status'>
                <span className='visually-hidden'>{i18n.language === 'en' ? 'Loading...' : 'جاري التحميل...'}</span>
              </Spinner>
              <p className='mt-2'>
                {i18n.language === 'en'
                  ? 'Loading cost vs sales analysis data...'
                  : 'جاري تحميل بيانات تحليل التكلفة مقابل المبيعات...'}
              </p>
            </div>
          ) : filteredData.length > 0 ? (
            <div className='table-responsive'>
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>{i18n.language === 'en' ? 'Item Code' : 'كود الصنف'}</th>
                    <th>{i18n.language === 'en' ? 'Item Name' : 'اسم الصنف'}</th>
                    <th>{i18n.language === 'en' ? 'Cost Price' : 'سعر التكلفة'}</th>
                    <th>{i18n.language === 'en' ? 'Sales Price' : 'سعر البيع'}</th>
                    <th>{i18n.language === 'en' ? 'Variance Amount' : 'مبلغ التفاوت'}</th>
                    <th>{i18n.language === 'en' ? 'Variance %' : 'نسبة التفاوت %'}</th>
                    <th>{i18n.language === 'en' ? 'Profitability' : 'الربحية'}</th>
                    <th>{i18n.language === 'en' ? 'Margin Status' : 'حالة الهامش'}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((item, index) => {
                    const profitability = getProfitabilityBadge(item.variancePercent);
                    const getMarginStatus = () => {
                      if (item.variancePercent > 30) return i18n.language === 'en' ? 'High Margin' : 'هامش عالي';
                      if (item.variancePercent > 15) return i18n.language === 'en' ? 'Good Margin' : 'هامش جيد';
                      if (item.variancePercent > 0) return i18n.language === 'en' ? 'Low Margin' : 'هامش منخفض';
                      if (item.variancePercent < -10) return i18n.language === 'en' ? 'Critical Loss' : 'خسارة حرجة';
                      return i18n.language === 'en' ? 'Needs Review' : 'يحتاج مراجعة';
                    };

                    const getMarginBadgeColor = () => {
                      if (item.variancePercent > 30) return 'success';
                      if (item.variancePercent > 15) return 'info';
                      if (item.variancePercent > 0) return 'warning';
                      if (item.variancePercent < -10) return 'danger';
                      return 'secondary';
                    };

                    return (
                      <tr key={`${item.itemId}-${index}`}>
                        <td className='fw-bold'>{item.itemCode}</td>
                        <td>
                          <div>{i18n.language === 'en' ? item.itemName : item.itemName2 || item.itemName}</div>
                          {item.itemName2 && (
                            <div className='text-muted small'>
                              {i18n.language === 'en' ? item.itemName2 : item.itemName}
                            </div>
                          )}
                        </td>
                        <td className='text-danger'>{formatCurrency(item.costPrice)}</td>
                        <td className='text-success'>{formatCurrency(item.salesPrice)}</td>
                        <td
                          className={`fw-bold ${
                            item.varianceAmount > 0
                              ? 'text-success'
                              : item.varianceAmount < 0
                              ? 'text-danger'
                              : 'text-warning'
                          }`}
                        >
                          {formatCurrency(item.varianceAmount)}
                        </td>
                        <td>
                          <Badge bg={getVarianceBadgeColor(item.variancePercent)}>
                            {formatPercentage(item.variancePercent)}
                          </Badge>
                        </td>
                        <td>
                          <Badge bg={profitability.color}>{profitability.text}</Badge>
                        </td>
                        <td>
                          <Badge bg={getMarginBadgeColor()}>{getMarginStatus()}</Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className='text-center py-5'>
              <p>
                {i18n.language === 'en'
                  ? 'No data available. Set your filters and click "Load Report" to generate.'
                  : 'لا توجد بيانات متاحة. قم بتعيين الفلاتر الخاصة بك وانقر على "تحميل التقرير" لإنشائه.'}
              </p>
            </div>
          )}
        </CardBody>

        {data.length > 0 && (
          <CardFooter className='text-muted'>
            <div className='row'>
              <div className='col-md-6'>
                <small>
                  <strong>{i18n.language === 'en' ? 'Analysis Summary:' : 'ملخص التحليل:'}</strong>{' '}
                  {i18n.language === 'en'
                    ? `${profitableItems} profitable items, ${lossItems} loss-making items, ${breakEvenItems} break-even items`
                    : `${profitableItems} عنصر مربح، ${lossItems} عنصر خاسر، ${breakEvenItems} عنصر متعادل`}
                </small>
              </div>
              <div className={`col-md-6 text-end  `}>
                <small>
                  {i18n.language === 'en'
                    ? `Report generated on ${new Date().toLocaleString()}`
                    : `تم إنشاء التقرير في ${new Date().toLocaleString()}`}
                </small>
              </div>
            </div>
          </CardFooter>
        )}
      </Card>
    </div>
  );
};

export default CostVsSalesReport;

// const CostVsSalesReport: React.FC = () => {
//   const [data, setData] = useState<CostVsSales[]>([]);
//   const [loading, setLoading] = useState<boolean>(false);
//   const [error, setError] = useState<string | null>(null);
//   const { t, i18n } = useTranslation();
//   const { ItemsOptions, loading: loading1, error: error1 } = useGetItemsList();
//   const [itemId, setItemId] = useState<string | null>('');

//   // Filter states
//   const [itemCode, setItemCode] = useState<string>('');
//   const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
//   const [startDate, endDate] = dateRange;
//   const [profitabilityFilter, setProfitabilityFilter] = useState<string>('all');
//   const [varianceThreshold, setVarianceThreshold] = useState<number>(0);

//   // Function to fetch data with filters
//   const fetchData = async () => {
//     setLoading(true);
//     setError(null);
//     try {
//       // Prepare query parameters
//       const params = new URLSearchParams();

//       if (itemCode) {
//         params.append('itemCode', itemCode);
//       }

//       if (startDate) {
//         params.append('fromDate', startDate.toISOString().split('T')[0]);
//       }

//       if (endDate) {
//         params.append('toDate', endDate.toISOString().split('T')[0]);
//       }

//       // API call with filters
//       const response = await ntxAPI.get<CostVsSales[]>(
//         `https://neatixapi.aljown.com/api/InventoryReports/cost-vs-sales?${params.toString()}`
//       );
//       setData(response.data?.data);
//     } catch (err) {
//       setError(err instanceof Error ? err.message : 'An unknown error occurred');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const fetchData2 = async () => {
//     setLoading(true);
//     setError(null);
//     try {
//       const { data } = await ntxAPI.get<APIResponse<CostVsSales[]>>(`InventoryReports/cost-vs-sales`, {
//         params: {
//           ItemId: itemId,

//           StartDate: startDate,
//           EndDate: endDate
//         }
//       });

//       if (data.succeeded === true) {
//         setData(data.data);
//       } else {
//         setError(data instanceof Error ? data.message : t('UnknownError'));
//       }
//     } catch (err) {
//       setError(err instanceof Error ? err.message : t('UnknownError'));
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Get unique items for dropdown (optional)
//   const getUniqueItems = () => {
//     const items = data.map(item => ({
//       id: item.itemId,
//       code: item.itemCode,
//       name: item.itemName
//     }));

//     return Array.from(new Map(items.map(item => [item.id, item])).values());
//   };

//   const resetFilters = () => {
//     setItemCode('');
//     setDateRange([null, null]);
//     setProfitabilityFilter('all');
//     setVarianceThreshold(0);
//   };

//   const formatCurrency = (amount: number): string => {
//     return new Intl.NumberFormat('en-US', {
//       style: 'currency',
//       currency: 'USD'
//     }).format(amount);
//   };

//   const formatPercentage = (percent: number): string => {
//     return `${percent.toFixed(2)}%`;
//   };

//   // Get profitability badge color
//   const getProfitabilityBadge = (variancePercent: number) => {
//     if (variancePercent > 0) return { color: 'success', text: 'Profitable' };
//     if (variancePercent < 0) return { color: 'danger', text: 'Loss' };
//     return { color: 'warning', text: 'Break Even' };
//   };

//   // Get variance badge color
//   const getVarianceBadgeColor = (variancePercent: number) => {
//     if (variancePercent > 20) return 'success';
//     if (variancePercent > 10) return 'info';
//     if (variancePercent > 0) return 'warning';
//     if (variancePercent < -10) return 'danger';
//     return 'secondary';
//   };

//   // Calculate profitability statistics
//   const calculateProfitabilityStats = () => {
//     const profitableItems = data.filter(item => item.variancePercent > 0).length;
//     const lossItems = data.filter(item => item.variancePercent < 0).length;
//     const breakEvenItems = data.filter(item => item.variancePercent === 0).length;
//     const totalVariance = data.reduce((sum, item) => sum + item.varianceAmount, 0);
//     const avgMargin = data.length > 0 ? data.reduce((sum, item) => sum + item.variancePercent, 0) / data.length : 0;

//     return { profitableItems, lossItems, breakEvenItems, totalVariance, avgMargin };
//   };

//   // Filter data based on profitability and variance threshold
//   const filteredData = data.filter(item => {
//     if (profitabilityFilter === 'profitable' && item.variancePercent <= 0) return false;
//     if (profitabilityFilter === 'loss' && item.variancePercent >= 0) return false;
//     if (profitabilityFilter === 'breakEven' && item.variancePercent !== 0) return false;
//     if (Math.abs(item.variancePercent) < varianceThreshold) return false;
//     return true;
//   });

//   const { profitableItems, lossItems, breakEvenItems, totalVariance, avgMargin } = calculateProfitabilityStats();

//   return (
//     <div className='container mt-4'>
//       <Card>
//         <CardHeader className='d-flex justify-content-between align-items-center'>
//           <h5 className='mb-0'>Cost vs Sales Analysis Report</h5>
//           {data.length > 0 && (
//             <div>
//               <span className='me-2'>
//                 Showing: {filteredData.length} of {data.length} items
//               </span>
//             </div>
//           )}
//         </CardHeader>

//         <CardBody>
//           {error && (
//             <Alert variant='danger' className='mb-4'>
//               Error loading data: {error}
//             </Alert>
//           )}

//           {/* Filters Section */}
//           <Card className='mb-4'>
//             <CardHeader>Filters</CardHeader>
//             <CardBody>
//               <Form>
//                 <Row className='g-3'>
//                   <Col md={3}>
//                     <Form.Group>
//                       <Form.Label>{t('itemName')}</Form.Label>
//                       <Select
//                         classNamePrefix='react-select'
//                         isSearchable
//                         isClearable
//                         options={ItemsOptions}
//                         onChange={(option: any) => setItemId(option?.value || null)}
//                         value={ItemsOptions.find(f => f.value === itemId)}
//                         placeholder={t('Choose')}
//                         menuPortalTarget={document.body}
//                         styles={{
//                           menuPortal: (base: any) => ({ ...base, zIndex: 9999 })
//                         }}
//                       />
//                     </Form.Group>
//                   </Col>

//                   <Col md={3}>
//                     <Form.Group>
//                       <Form.Label>{t('DateRange')}</Form.Label>
//                       <DatePicker
//                         selectsRange={true}
//                         startDate={startDate}
//                         endDate={endDate}
//                         onChange={update => setDateRange(update)}
//                         isClearable={true}
//                         className='form-control text-center'
//                         placeholderText={t('SelectDateRange')}
//                       />
//                     </Form.Group>
//                   </Col>

//                   <Col md={3}>
//                     <Form.Group controlId='profitabilityFilter'>
//                       <Form.Label>Profitability</Form.Label>
//                       <Form.Select value={profitabilityFilter} onChange={e => setProfitabilityFilter(e.target.value)}>
//                         <option value='all'>All Items</option>
//                         <option value='profitable'>Profitable Only</option>
//                         <option value='loss'>Loss Making Only</option>
//                         <option value='breakEven'>Break Even Only</option>
//                       </Form.Select>
//                     </Form.Group>
//                   </Col>

//                   <Col md={3}>
//                     <Form.Group controlId='varianceThresholdFilter'>
//                       <Form.Label>Variance Threshold (%)</Form.Label>
//                       <Form.Range
//                         min='0'
//                         max='100'
//                         step='1'
//                         value={varianceThreshold}
//                         onChange={e => setVarianceThreshold(Number(e.target.value))}
//                       />
//                       <Form.Text className='text-muted'>Min variance: {varianceThreshold}%</Form.Text>
//                     </Form.Group>
//                   </Col>

//                   <Col xs={12} className='text-end'>
//                     <Button variant='secondary' onClick={resetFilters} className='me-2'>
//                       Reset Filters
//                     </Button>
//                     <Button variant='primary' onClick={fetchData2} disabled={loading}>
//                       {loading ? (
//                         <>
//                           <Spinner
//                             as='span'
//                             animation='border'
//                             size='sm'
//                             role='status'
//                             aria-hidden='true'
//                             className='me-2'
//                           />
//                           Loading...
//                         </>
//                       ) : (
//                         'Load Report'
//                       )}
//                     </Button>
//                   </Col>
//                 </Row>
//               </Form>
//             </CardBody>
//           </Card>

//           {/* Profitability Summary */}
//           {data.length > 0 && (
//             <Row className='mb-4'>
//               <Col md={2}>
//                 <Card className='text-center border-0 bg-secondary bg-opacity-30'>
//                   <CardBody>
//                     <CardTitle className='text-primary'>Total Items</CardTitle>
//                     <h3 className='text-primary'>{data.length}</h3>
//                   </CardBody>
//                 </Card>
//               </Col>
//               <Col md={2}>
//                 <Card className='text-center border-0 bg-secondary bg-opacity-30'>
//                   <CardBody>
//                     <CardTitle className='text-success'>Profitable</CardTitle>
//                     <h3 className='text-success'>{profitableItems}</h3>
//                     <small>{((profitableItems / data.length) * 100).toFixed(1)}%</small>
//                   </CardBody>
//                 </Card>
//               </Col>
//               <Col md={2}>
//                 <Card className='text-center border-0 bg-secondary bg-opacity-30'>
//                   <CardBody>
//                     <CardTitle className='text-danger'>Loss Making</CardTitle>
//                     <h3 className='text-danger'>{lossItems}</h3>
//                     <small>{((lossItems / data.length) * 100).toFixed(1)}%</small>
//                   </CardBody>
//                 </Card>
//               </Col>
//               <Col md={2}>
//                 <Card className='text-center border-0 bg-secondary bg-opacity-30'>
//                   <CardBody>
//                     <CardTitle className='text-warning'>Break Even</CardTitle>
//                     <h3 className='text-warning'>{breakEvenItems}</h3>
//                     <small>{((breakEvenItems / data.length) * 100).toFixed(1)}%</small>
//                   </CardBody>
//                 </Card>
//               </Col>
//               <Col md={2}>
//                 <Card className='text-center border-0 bg-secondary bg-opacity-30'>
//                   <CardBody>
//                     <CardTitle className='text-info'>Total Variance</CardTitle>
//                     <h3 className={totalVariance >= 0 ? 'text-success' : 'text-danger'}>
//                       {formatCurrency(totalVariance)}
//                     </h3>
//                   </CardBody>
//                 </Card>
//               </Col>
//               <Col md={2}>
//                 <Card className='text-center border-0 bg-secondary bg-opacity-30'>
//                   <CardBody>
//                     <CardTitle className='text-secondary'>Avg Margin</CardTitle>
//                     <h3 className={avgMargin >= 0 ? 'text-success' : 'text-danger'}>{formatPercentage(avgMargin)}</h3>
//                   </CardBody>
//                 </Card>
//               </Col>
//             </Row>
//           )}

//           {/* Profitability Distribution */}
//           {data.length > 0 && (
//             <Card className='mb-4'>
//               <CardHeader>
//                 <h6 className='mb-0'>Profitability Distribution</h6>
//               </CardHeader>
//               <CardBody>
//                 <Row className='align-items-center'>
//                   <Col md={3}>
//                     <div className='text-center'>
//                       <div className='mb-2'>
//                         <Badge bg='success' className='me-2'>
//                           Profitable
//                         </Badge>
//                         <Badge bg='danger' className='me-2'>
//                           Loss
//                         </Badge>
//                         <Badge bg='warning'>Break Even</Badge>
//                       </div>
//                     </div>
//                   </Col>
//                   <Col md={9}>
//                     <ProgressBar style={{ height: '25px' }}>
//                       <ProgressBar
//                         variant='success'
//                         now={(profitableItems / data.length) * 100}
//                         key={1}
//                         label={`${profitableItems} Profitable`}
//                       />
//                       <ProgressBar
//                         variant='danger'
//                         now={(lossItems / data.length) * 100}
//                         key={2}
//                         label={`${lossItems} Loss`}
//                       />
//                       <ProgressBar
//                         variant='warning'
//                         now={(breakEvenItems / data.length) * 100}
//                         key={3}
//                         label={`${breakEvenItems} Break Even`}
//                       />
//                     </ProgressBar>
//                   </Col>
//                 </Row>
//               </CardBody>
//             </Card>
//           )}

//           {/* Results Section */}
//           {loading ? (
//             <div className='text-center my-5'>
//               <Spinner animation='border' role='status'>
//                 <span className='visually-hidden'>Loading...</span>
//               </Spinner>
//               <p className='mt-2'>Loading cost vs sales analysis data...</p>
//             </div>
//           ) : filteredData.length > 0 ? (
//             <div className='table-responsive'>
//               <Table striped bordered hover>
//                 <thead>
//                   <tr>
//                     <th>Item Code</th>
//                     <th>Item Name</th>
//                     <th>Cost Price</th>
//                     <th>Sales Price</th>
//                     <th>Variance Amount</th>
//                     <th>Variance %</th>
//                     <th>Profitability</th>
//                     <th>Margin Status</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {filteredData.map((item, index) => {
//                     const profitability = getProfitabilityBadge(item.variancePercent);
//                     return (
//                       <tr key={`${item.itemId}-${index}`}>
//                         <td className='fw-bold'>{item.itemCode}</td>
//                         <td>
//                           <div>{item.itemName}</div>
//                           {item.itemName2 && <div className='text-muted small'>{item.itemName2}</div>}
//                         </td>
//                         <td className='text-danger'>{formatCurrency(item.costPrice)}</td>
//                         <td className='text-success'>{formatCurrency(item.salesPrice)}</td>
//                         <td
//                           className={`fw-bold ${
//                             item.varianceAmount > 0
//                               ? 'text-success'
//                               : item.varianceAmount < 0
//                               ? 'text-danger'
//                               : 'text-warning'
//                           }`}
//                         >
//                           {formatCurrency(item.varianceAmount)}
//                         </td>
//                         <td>
//                           <Badge bg={getVarianceBadgeColor(item.variancePercent)}>
//                             {formatPercentage(item.variancePercent)}
//                           </Badge>
//                         </td>
//                         <td>
//                           <Badge bg={profitability.color}>{profitability.text}</Badge>
//                         </td>
//                         <td>
//                           {item.variancePercent > 30 ? (
//                             <Badge bg='success'>High Margin</Badge>
//                           ) : item.variancePercent > 15 ? (
//                             <Badge bg='info'>Good Margin</Badge>
//                           ) : item.variancePercent > 0 ? (
//                             <Badge bg='warning'>Low Margin</Badge>
//                           ) : item.variancePercent < -10 ? (
//                             <Badge bg='danger'>Critical Loss</Badge>
//                           ) : (
//                             <Badge bg='secondary'>Needs Review</Badge>
//                           )}
//                         </td>
//                       </tr>
//                     );
//                   })}
//                 </tbody>
//               </Table>
//             </div>
//           ) : (
//             <div className='text-center py-5'>
//               <p>No data available. Set your filters and click "Load Report" to generate.</p>
//             </div>
//           )}
//         </CardBody>

//         {data.length > 0 && (
//           <CardFooter className='text-muted'>
//             <div className='row'>
//               <div className='col-md-6'>
//                 <small>
//                   <strong>Analysis Summary:</strong> {profitableItems} profitable items, {lossItems} loss-making items,{' '}
//                   {breakEvenItems} break-even items
//                 </small>
//               </div>
//               <div className='col-md-6 text-end'>
//                 <small>Report generated on {new Date().toLocaleString()}</small>
//               </div>
//             </div>
//           </CardFooter>
//         )}
//       </Card>
//     </div>
//   );
// };
